import type { AgentInput, ApiNodeDataAiDecision } from '@onefootprint/request-types/dashboard';
import type { DecisionInputs } from '../types';

type WireDecisionInputValue = {
  expression?: unknown;
  comment?: unknown;
};

const toWireDecisionInputValue = (value: unknown): WireDecisionInputValue | undefined => {
  if (typeof value !== 'object' || value === null) return undefined;
  return value as WireDecisionInputValue;
};

export const parseInputsToDecisionInputs = (inputs: ApiNodeDataAiDecision['inputs']): DecisionInputs => {
  return Object.entries(inputs).reduce((acc, [key, value]) => {
    const wireValue = toWireDecisionInputValue(value);
    if (!wireValue) return acc;
    const comment = typeof wireValue.comment === 'string' ? wireValue.comment : undefined;
    const expression = typeof wireValue.expression === 'string' ? wireValue.expression : '';
    acc.push({ key, expression, comment });
    return acc;
  }, [] as DecisionInputs);
};

export const parseDecisionInputsToInputs = (
  inputs: DecisionInputs,
  declared: AgentInput[],
): ApiNodeDataAiDecision['inputs'] => {
  const optionalKeys = new Set(declared.filter(input => !input.isRequired).map(input => input.fieldName));
  return inputs.reduce(
    (acc, input) => {
      // A blank expression on an optional input means unwired. Omit the key entirely — the backend
      // binds it to null, whereas an empty expression is a validation error.
      if (!input.expression.trim() && optionalKeys.has(input.key)) return acc;
      acc[input.key] = {
        expression: input.expression,
        comment: input.comment,
      };
      return acc;
    },
    {} as ApiNodeDataAiDecision['inputs'],
  );
};

// An unwired optional input is stored as an absent key, so its row has to be materialized from the
// declared schema — otherwise it vanishes on reopen with no way to wire it up later. Declared order
// also settles the row order, since the stored wire map is unordered.
export const alignInputsToDeclared = (inputs: DecisionInputs, declared: AgentInput[]): DecisionInputs => {
  const byKey = new Map(inputs.map(input => [input.key, input]));
  const result: DecisionInputs = declared.map(
    ({ fieldName, comment }) => byKey.get(fieldName) ?? { key: fieldName, expression: '', comment },
  );

  const declaredKeys = new Set(declared.map(d => d.fieldName));
  for (const input of inputs) {
    if (!declaredKeys.has(input.key)) result.push(input);
  }
  return result;
};
